import {
    ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener,
    Input, OnChanges, OnDestroy, OnInit, Output, QueryList, SimpleChanges, ViewChild, ViewChildren, ViewContainerRef
} from '@angular/core';
import { ImageService } from '../../services/image-gallery.service';
import { Subscription } from 'rxjs/internal/Subscription';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { DataService } from '../../../../services/data.service';
import { UserService } from '../../../../services/user.service';
import { MatDialog } from '@angular/material/dialog';
import { DeleteConfirmationComponent } from '../../../../../../app/partials/dialog/builder/delete-confirmation/delete-confirmation.component';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-gallery',
    templateUrl: './gallery.component.html',
    styleUrls: ['./gallery.component.scss']
})
export class GalleryComponent implements OnInit, OnDestroy, OnChanges {
    gallery: Array<any> = [];
    imageDataStaticPath: string = 'https://s3.amazonaws.com/ai-images.sitecreator.io/assets/img/gallery/';
    imageDataCompletePath: string = '';
    deletedImageId: string = '';
    dataFileName: string = 'data.json';
    images: Array<any> = [];
    minimalQualityCategory = 'preview_xxs';
    viewerSubscription: Subscription;
    rowIndex: number = 0;
    rightArrowInactive: boolean = false;
    leftArrowInactive: boolean = false;
    altImages: string;

    // tslint:disable-next-line:no-input-rename
    @Input('flexBorderSize') providedImageMargin: number = 3;
    // tslint:disable-next-line:no-input-rename
    @Input('flexImageSize') providedImageSize: number = 7;
    // Client name, as in imageDataStaticPath + providedGalleryName / dataFileName
    // tslint:disable-next-line:no-input-rename
    @Input('galleryName') providedGalleryName: string = '';
    @Input('galleryTitle') galleryTitle: string = '';
    @Input('galleryId') galleryId: string = '';
    @Input('siteName') siteName: string = '';
    @Input('siteId') siteId: string = '';
    // tslint:disable-next-line:no-input-rename
    @Input('metadataUri') providedMetadataUri: string = undefined;
    // tslint:disable-next-line:no-input-rename
    @Input('maxRowsPerPage') rowsPerPage: number = 200;

    @Output() viewerChange = new EventEmitter<boolean>();
    @Output() imageDeleted = new EventEmitter<object>();
    @Output() NumberOfImages = new EventEmitter<number>();

    @ViewChild('galleryContainer') galleryContainer: ElementRef;

    @ViewChildren('imageElement') imageElements: QueryList<any>;

    @HostListener('window:scroll', ['$event']) triggerCycle(event: any): void {
        this.scaleGallery();
    }

    @HostListener('window:resize', ['$event']) windowResize(event: any): void {
        this.render();
    }

    constructor(
        private dialog: MatDialog,
        public imageService: ImageService,
        public http: HttpClient,
        public changeDetectorRef: ChangeDetectorRef,
        private dataService: DataService,
        private userService: UserService,
    ) {
    }

    ngOnInit(): void {
        this.fetchDataAndRender();

        this.viewerSubscription = this.imageService.showImageViewerChanged$
            .subscribe((visibility: boolean) => this.viewerChange.emit(visibility));

    }

    ngOnChanges(changes: SimpleChanges): void {
        // input params changed
        if (changes['providedMetadataUri'] !== undefined) {
            this.fetchDataAndRender();
        } else {
            this.render();
        }
    }

    ngOnDestroy(): void {
        if (this.viewerSubscription) {
            this.viewerSubscription.unsubscribe();
        }
    }

    openImageViewer(img: any): void {
        this.imageService.updateImages(this.images);
        this.imageService.updateSelectedImageIndex(this.images.indexOf(img));
        this.imageService.showImageViewer(true);
    }

    /**
     * direction (-1: left, 1: right)
     */
    navigate(direction: number): void {
        if ((direction === 1 && this.rowIndex < this.gallery.length - this.rowsPerPage)
            || (direction === -1 && this.rowIndex > 0)) {
            this.rowIndex += (this.rowsPerPage * direction);
        }
        this.refreshNavigationErrorState();
    }

    calcImageMargin(): number {
        const galleryWidth = this.getGalleryWidth();
        const ratio = galleryWidth / 1920;
        return Math.round(Math.max(1, this.providedImageMargin * ratio));
    }

    private fetchDataAndRender(): void {
        this.imageDataCompletePath = this.providedMetadataUri;

        if (!this.providedMetadataUri) {
            this.imageDataCompletePath = this.providedGalleryName !== '' ?
                `${this.imageDataStaticPath + this.providedGalleryName}/${this.dataFileName}` :
                this.imageDataStaticPath + this.dataFileName;
        }
        this.dataService.getImagesGallery(this.imageDataCompletePath)
            .subscribe(
                (data: Array<any>) => {
                    let reg = /https:\/\/dev-sitecreator-v1-serverless-s3-gallery-bucket.s3.amazonaws.com/g;
                    let strData = JSON.stringify(data).replace(reg, environment.cloudFrontGalleryLinkDev);
                    reg = /https:\/\/prod-sitecreator-v1-serverless-s3-gallery-bucket.s3.amazonaws.com/g;
                    strData = strData.replace(reg, environment.cloudFrontGalleryLinkProd);
                    data = JSON.parse(strData);
                    this.images = data;
                    this.imageService.updateImages(this.images);

                    this.images.forEach(image => {
                        // image['galleryImageLoaded'] = false;
                        image['galleryImageLoaded'] = true;
                        image['viewerImageLoaded'] = false;
                        image['srcAfterFocus'] = '';
                    });
                    let stockImages = this.images.filter(image => image.preview_l && image.preview_l.path && !image.preview_l.path.includes('defaultGalleryImage'));
                    this.NumberOfImages.emit(stockImages.length);
                    // twice, single leads to different strange browser behaviour
                    // this.render();
                    this.render();
                },
                err => {
                    if (this.providedMetadataUri) {
                        console.error(`HTTP Error while trying to retrieve  '${this.providedMetadataUri}' Metadata Json File: ${err}`);
                    } else {
                        console.error(`Error during http request for Image Data JSON: ${err}`);
                    }

                });
    }

    private render(): void {
        this.gallery = [];

        let tempRow = [this.images[0]];
        let currentRowIndex = 0;
        let i = 0;

        // tip: use a variable for images.length for performance
        for (i; i < this.images.length; i++) {
            while (this.images[i + 1] && this.shouldAddCandidate(tempRow, this.images[i + 1])) {
                i++;
            }
            if (this.images[i + 1]) {
                tempRow.pop();
            }
            this.gallery[currentRowIndex++] = tempRow;

            tempRow = [this.images[i + 1]];
        }
        this.scaleGallery();
    }

    private shouldAddCandidate(imgRow: Array<any>, candidate: any): boolean {
        const oldDifference = this.calcIdealHeight() - this.calcRowHeight(imgRow);
        imgRow.push(candidate);
        const newDifference = this.calcIdealHeight() - this.calcRowHeight(imgRow);

        return Math.abs(oldDifference) > Math.abs(newDifference);
    }

    private calcRowHeight(imgRow: Array<any>): number {
        const originalRowWidth = this.calcOriginalRowWidth(imgRow);

        const ratio = (this.getGalleryWidth() - (imgRow.length - 1) * this.calcImageMargin()) / originalRowWidth;
        const rowHeight = imgRow[0][this.minimalQualityCategory]['height'] * ratio;

        return rowHeight;
    }

    private calcOriginalRowWidth(imgRow: Array<any>): number {
        let originalRowWidth = 0;
        imgRow.forEach(img => {
            const individualRatio = this.calcIdealHeight() / img[this.minimalQualityCategory]['height'];
            img[this.minimalQualityCategory]['width'] = img[this.minimalQualityCategory]['width'] * individualRatio;
            img[this.minimalQualityCategory]['height'] = this.calcIdealHeight();
            originalRowWidth += img[this.minimalQualityCategory]['width'];
        });

        return originalRowWidth;
    }

    private calcIdealHeight(): number {
        return this.getGalleryWidth() / (80 / this.providedImageSize) + 100;
    }

    private getGalleryWidth(): number {
        if (this.galleryContainer && this.galleryContainer.nativeElement.clientWidth === 0) {
            // for IE11
            return this.galleryContainer.nativeElement.scrollWidth;
        }
        return this.galleryContainer?this.galleryContainer.nativeElement.clientWidth:this.galleryContainer;
    }

    private scaleGallery(): void {
        let imageCounter = 0;
        let maximumGalleryImageHeight = 0;

        let ratio;
        this.gallery.slice(this.rowIndex, this.rowIndex + this.rowsPerPage)
            .forEach(imgRow => {
                const originalRowWidth = this.calcOriginalRowWidth(imgRow);
                if (imgRow !== this.gallery[this.gallery.length - 1]) {
                    ratio = (this.getGalleryWidth() - (imgRow.length - 1) * this.calcImageMargin()) / originalRowWidth;
                    imgRow.forEach((img: any) => {
                        img['width'] = img[this.minimalQualityCategory]['width'] * ratio;
                        img['height'] = img[this.minimalQualityCategory]['height'] * ratio;
                        maximumGalleryImageHeight = Math.max(maximumGalleryImageHeight, img['height']);
                        this.checkForAsyncLoading(img, imageCounter++);
                    });
                } else {
                    if (this.gallery.length == 1) {
                        ratio = (this.getGalleryWidth() - (imgRow.length - 1) * this.calcImageMargin()) / originalRowWidth;
                    }
                    imgRow.forEach((img: any) => {
                        img.width = img[this.minimalQualityCategory]['width'] * ratio;
                        img.height = img[this.minimalQualityCategory]['height'] * ratio;
                        maximumGalleryImageHeight = Math.max(maximumGalleryImageHeight, img['height']);
                        this.checkForAsyncLoading(img, imageCounter++);
                    });
                }
            });
        this.minimalQualityCategory = maximumGalleryImageHeight > 375 ? 'preview_xs' : 'preview_xxs';
        this.refreshNavigationErrorState();

        this.changeDetectorRef.detectChanges();
    }

    private checkForAsyncLoading(image: any, imageCounter: number): void {
        const imageElements = this.imageElements?this.imageElements.toArray():[];

        if (image['galleryImageLoaded'] ||
            (imageElements.length > 0 &&
                imageElements[imageCounter] &&
                this.isScrolledIntoView(imageElements[imageCounter].nativeElement))) {
            image['galleryImageLoaded'] = true;
            image['srcAfterFocus'] = image[this.minimalQualityCategory]['path'];
        } else {
            image['srcAfterFocus'] = '';
        }
    }

    private isScrolledIntoView(element: any): boolean {
        const elementTop = element.getBoundingClientRect().top;
        const elementBottom = element.getBoundingClientRect().bottom;
        return elementTop < window.innerHeight && elementBottom >= 0 && (elementBottom > 0 || elementTop > 0);
    }

    private refreshNavigationErrorState(): void {
        this.leftArrowInactive = this.rowIndex === 0;
        this.rightArrowInactive = this.rowIndex > (this.gallery.length - this.rowsPerPage);
    }

    updateUrl($event) {
        const src = $event.target.src;
        $event.target.src = 'https://s3.amazonaws.com/ai-images.sitecreator.io/assets/img/gallery/loading.gif';
        setTimeout(() => {
            $event.target.src = src;
        }, 8000);
    }
    deleteImage(img: any) {

        this.dialog.open(DeleteConfirmationComponent, { data: { position: -1, message: "Are you sure you want to delete this image ?", buttonText: "Delete image" }, panelClass: 'p-dialog' }).afterClosed().subscribe(res => {
            if (res) {
                this.deletedImageId = img.imageId;
                const images = {
                    galleryId: this.galleryId,
                    images: [img],
                    dataFile: this.providedMetadataUri.split('/').pop(),
                    userEmail: this.userService.getUserData().email,
                    siteId: this.siteId
                };
                this.dataService.deleteImagesGallery(images).subscribe((response: { title: string, dataUrl: string, galleryId: string }) => {
                    const res = {
                        save: true,
                        action: 'delete',
                        dataUrl: response.dataUrl,
                        galleryId: response.galleryId
                    };
                    this.imageDeleted.emit(res);
                    this.deletedImageId = '';
                });
            }
        });
    }
}
